import {
  getCurrencyDrawerOptions,
  getExtendedDurationComponents,
  ModeEnum,
  transformDrawerValueToNumber,
  type ColumnDef,
  type Currency,
  type ICustomerNetAcquisitionLimit,
  type InputsAndDropdownsDrawerDropdownOption,
  type InputsAndDropdownsDrawerOption,
  type SizeValue,
} from '@talos/kyoko';
import type { ValueGetterParams } from 'ag-grid-enterprise';

type RejectThresholdValueGetterParams = ValueGetterParams<
  ICustomerNetAcquisitionLimit,
  ICustomerNetAcquisitionLimit['RejectThreshold']
>;

const rejectThresholdValueGetter =
  (currenciesBySymbol: Map<string, Currency>) =>
  (params: RejectThresholdValueGetterParams): SizeValue => {
    const value = params.data?.RejectThreshold;

    if (!value) {
      return { value: 'Unlimited', currency: '', increment: undefined };
    }

    const thresholdAssetSymbol = params.data?.ThresholdAsset ?? '';
    const thresholdCurrency = currenciesBySymbol.get(thresholdAssetSymbol);

    const increment = thresholdCurrency?.DefaultIncrement;
    const currency = thresholdCurrency?.Symbol ?? thresholdAssetSymbol ?? '';

    return { value, currency, increment };
  };

export function getAcquisitionLimitColumns(currenciesBySymbol: Map<string, Currency>) {
  return [
    {
      type: 'text',
      field: 'Name',
      sort: '+',
    },
    {
      type: 'currency',
      field: 'ThresholdAsset',
    },
    {
      type: 'size',
      field: 'RejectThreshold',
      params: { valueGetter: rejectThresholdValueGetter(currenciesBySymbol) },
    },
    {
      type: 'extendedDuration',
      field: 'WindowDuration',
    },
    {
      type: 'text',
      field: 'Description',
    },
  ] as ColumnDef<ICustomerNetAcquisitionLimit>[];
}

export interface AcquisitionLimitDrawerEntity extends Omit<ICustomerNetAcquisitionLimit, 'WindowDuration'> {
  _LimitType: 'limited' | 'unlimited';
  _WindowDurationUnit: string;
  _WindowDurationLength: number;
}

const WINDOW_UNITS: InputsAndDropdownsDrawerDropdownOption[] = [
  {
    label: 'Days',
    value: 'D',
  },
  {
    label: 'Months',
    value: 'M',
  },
  {
    label: 'Years',
    value: 'Y',
  },
];

function getWindowUnitSuffix(value: string): string {
  const unit = WINDOW_UNITS.find(u => u.value === value);
  return unit?.label ?? '';
}

function isUnlimited(limit: AcquisitionLimitDrawerEntity) {
  return limit._LimitType === 'unlimited';
}

export function getAcquisitionLimitDrawerOptions(
  currencies: Currency[]
): InputsAndDropdownsDrawerOption<AcquisitionLimitDrawerEntity>[] {
  return [
    {
      field: 'Name',
      type: 'input',
      title: 'Limit Name',
      getIsRequired: () => true,
      disabledWhenEditing: true,
    },
    {
      field: '_LimitType',
      type: 'toggle',
      title: 'Limit Type',
      tooltip: 'Create a unlimited limit to disable a default limit for some subset of customers',
      getToggleOptions: () => [
        {
          displayName: 'Fixed Amount',
          value: 'limited',
        },
        {
          displayName: 'Unlimited',
          value: 'unlimited',
        },
      ],
      getIsRequired: () => true,
    },
    {
      field: 'ThresholdAsset',
      type: 'dropdown',
      getDropdownOptions: () => getCurrencyDrawerOptions(currencies),
      getIsDisabled: form => !form._LimitType || isUnlimited(form),
      getIsRequired: form => !isUnlimited(form),
    },
    {
      field: 'RejectThreshold',
      type: 'inputNumeric',
      title: 'Reject Threshold',
      getInputSuffix: form => form.ThresholdAsset,
      getIsDisabled: form => !form.ThresholdAsset || isUnlimited(form),
      getIsRequired: form => !isUnlimited(form),
    },
    {
      field: '_WindowDurationUnit',
      type: 'dropdown',
      title: 'Window Unit',
      getDropdownOptions: () => WINDOW_UNITS,
      getIsRequired: () => true,
    },
    {
      field: '_WindowDurationLength',
      type: 'inputNumeric',
      title: 'Window Length',
      getInputSuffix: form => getWindowUnitSuffix(form._WindowDurationUnit),
      getIsDisabled: form => !form._WindowDurationUnit,
      getIsRequired: () => true,
      transformControlToDrawerRecord: transformDrawerValueToNumber,
    },
    {
      field: 'Description',
      type: 'input',
      title: 'Description',
    },
  ];
}

export function getAcquisitionLimitPostEntity(entity: AcquisitionLimitDrawerEntity): ICustomerNetAcquisitionLimit {
  // Enable by default
  if (!entity.Mode) {
    entity.Mode = ModeEnum.Enabled;
  }

  if (entity._LimitType === 'unlimited') {
    entity.RejectThreshold = undefined;
  }

  return {
    ...entity,
    WindowDuration: entity._WindowDurationLength + entity._WindowDurationUnit,
  };
}

export function getAcquisitionLimitAsDrawerEntity(limit: ICustomerNetAcquisitionLimit): AcquisitionLimitDrawerEntity {
  const durationComponents = getExtendedDurationComponents(limit.WindowDuration);
  if (durationComponents.length !== 1) {
    throw new Error('Limit window duration can only be composed of a single component');
  }
  const [unit, length] = durationComponents[0];
  if (!['D', 'M', 'Y'].includes(unit)) {
    throw new Error('Limit window duration is composed of a unsupported single component');
  }

  const isUnlimited = !limit.RejectThreshold;

  return {
    ...limit,
    _LimitType: isUnlimited ? 'unlimited' : 'limited',
    _WindowDurationUnit: unit,
    _WindowDurationLength: length,
  };
}
